import { useState } from 'react'

import type { SerializedError } from '@reduxjs/toolkit'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { skipToken } from '@reduxjs/toolkit/query/react'

import { coreApi, useGetTrackedEventQuery } from '../../../api/core'
import { getHttpErrorStatus } from '../utils/httpErrorStatus'

const REFUSAL_STATUSES = [400, 401, 403, 404]

const isRefusal = (error: FetchBaseQueryError | SerializedError | undefined) => {
  const status = getHttpErrorStatus(error)
  return status !== undefined && REFUSAL_STATUSES.includes(status)
}

// `fetchBaseQuery` reports a request the browser could not complete as `FETCH_ERROR`, with no HTTP status. That includes
// a response the browser hides from the page: an error written by a gateway or edge without CORS headers can be blocked
// by the browser, and DevTools then shows the HTTP status while the page only sees `FETCH_ERROR`. This is the suspected
// cause of the duplicate request on a shared link to a game outside the user's access; it has not been confirmed with a
// credentialed check against QA.
const isUnreadableFailure = (error: unknown) => (error as { status?: unknown } | undefined)?.status === 'FETCH_ERROR'

const joinsFailure = (error: FetchBaseQueryError | SerializedError | undefined) => isRefusal(error) || isUnreadableFailure(error)

type UseTrackedEventOptions = {
  skip?: boolean
}

/**
 * `useGetTrackedEventQuery` for the components of the Event dialog, which all read the same cache entry.
 *
 * RTK Query re-requests an entry that failed every time a new subscriber arrives. The dialog and its
 * tabs mount after the container's access check, so for an event the backend refuses (400 on a shared
 * link to a game outside the user's access) each of them would fire the request again. A component
 * that mounts, or is pointed at another event, while the entry is already in a refusal error (400, 401,
 * 403 or 404, whatever the body of the response) therefore reads the failure from
 * the cache without subscribing. Components that were already subscribed when it failed keep their
 * subscription, and a different event always starts its own request. Other failures with a readable status
 * (5xx) keep RTK Query's retry on a new subscriber.
 *
 * A failure with no readable status (`FETCH_ERROR`) is joined like a refusal. The page cannot tell a CORS-blocked 400
 * from a lost connection, and treating it as retryable made the dialog repeat the container's failed check the moment
 * it mounted: two identical GETs. Trade-off: the dialog no longer retries a genuine network failure on mount.
 * Reloading, or reopening the event once the cache has dropped the entry (60s after the dialog closes), does; until
 * then a reopened dialog joins the cached failure. The same holds for a gateway 502/504 without CORS headers, which is
 * also a `FETCH_ERROR`. The root fix is for the gateway to send CORS headers on its error responses, so that the 400
 * is readable and recognised as a refusal.
 *
 * Switching a component on after it was skipped counts as arriving: a check that is turned off and on again
 * while the event was refused in between (its condition depends on queries that RTK Query re-requests) joins the
 * refusal instead of requesting the event a second time.
 *
 * Joining only lasts while the cache holds the refusal. Once the entry is dropped (RTK removes it 60s
 * after its last subscriber leaves) or is being refetched, the component subscribes normally.
 */
export const useTrackedEvent = (eventId: string | undefined, { skip = false }: UseTrackedEventOptions = {}) => {
  const enabled = Boolean(eventId) && !skip
  const cached = coreApi.endpoints.getTrackedEvent.useQueryState(enabled && eventId ? eventId : skipToken)

  const [arrival, setArrival] = useState<{ eventId?: string; afterFailure: boolean }>({ afterFailure: false })
  // Resetting the event id to undefined forces the join decision to be made again when the hook is switched back on.
  if (!enabled && arrival.eventId !== undefined) {
    setArrival({ afterFailure: false })
  }
  if (enabled && arrival.eventId !== eventId) {
    setArrival({ eventId, afterFailure: cached.isError && joinsFailure(cached.error) })
  }
  if (enabled && arrival.eventId === eventId && arrival.afterFailure && !cached.isError) {
    setArrival({ eventId, afterFailure: false })
  }
  const joinsFailedEntry = enabled && arrival.eventId === eventId && arrival.afterFailure

  const query = useGetTrackedEventQuery(eventId as string, { skip: !enabled || joinsFailedEntry })

  return joinsFailedEntry ? cached : query
}
